import CancelIcon from '@mui/icons-material/Cancel';
import {
    Alert,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    Grid,
    Typography,
    Box,
    Tooltip
} from '@mui/material';
import { FC, useCallback, useMemo, useState, useEffect } from 'react';
import { RespondentDto, SurveyPayloadDto } from '../../../dtos';
import { useAddRespondentsToSurveyMutation } from '../../../store';
import { useGetScheduleQuery } from '../../../store/api/schedule-api';
import { StandardDialogHeader, useFailedActionSnackbar, useSuccessfulActionSnackbar } from '../../CoreLib/library';
import { CompanyRespondentSelect } from '../../CommonInputs/CompanyRespondentSelect';
import { Info, PersonAdd } from '@mui/icons-material';

interface IAddRespondentsToSurveyModalProps {
    scheduleId: string;
    open: boolean;
    onClose: () => void;
}

export const AddRespondentsToSurveyModal: FC<IAddRespondentsToSurveyModalProps> = ({ scheduleId, open, onClose }) => {
    const [currentRespondentId, setCurrentRespondentId] = useState('');
    const [selectedRespondents, setSelectedRespondents] = useState<RespondentDto[]>([]);

    const { data: schedule, isLoading: isLoadingSchedule, isError: isErrorLoadingSchedule } = useGetScheduleQuery(scheduleId);

    const companyId = useMemo(() => schedule?.companyId || '', [schedule?.companyId]);
    const surveyId = useMemo(() => schedule?.surveyId || '', [schedule?.surveyId]);

    const existingRespondents = useMemo(() => {
        try {
            if (schedule?.survey?.surveyPayload) {
                const payload: SurveyPayloadDto = JSON.parse(schedule.survey.surveyPayload);
                return payload.respondentQueue || [];
            }
        } catch (error) {
            console.error('Failed to parse survey payload:', error);
        }
        return [];
    }, [schedule?.survey?.surveyPayload]);

    const existingRespondentIds = useMemo(() => {
        return new Set(existingRespondents.map(r => r.id).filter(Boolean));
    }, [existingRespondents]);

    useEffect(() => {
        if (!open) {
            setSelectedRespondents([]);
            setCurrentRespondentId('');
        }
    }, [open, schedule?.id]);

    const [addRespondentsToSurvey, {
        isLoading: isAddingRespondents,
        isSuccess: isAddRespondentsSuccessful,
        isError: isAddRespondentsError
    }] = useAddRespondentsToSurveyMutation();

    useSuccessfulActionSnackbar('add', 'respondents to survey', isAddRespondentsSuccessful, () => {
        onClose();
        setSelectedRespondents([]);
        setCurrentRespondentId('');
    });

    useFailedActionSnackbar('add', 'respondents to survey', isAddRespondentsError);

    const handleRespondentSelected = useCallback((respondent?: RespondentDto) => {
        if (respondent && !selectedRespondents.some(r => r.id === respondent.id)) {
            setSelectedRespondents(prev => [...prev, respondent]);
            setCurrentRespondentId(''); // Reset the select
        }
    }, [selectedRespondents]);

    const removeFromSelectedRespondents = useCallback((respondent: RespondentDto) => {
        setSelectedRespondents(prev => prev.filter(r => r.id !== respondent.id));
    }, []);

    const handleAddRespondents = useCallback(async () => {
        if (selectedRespondents.length === 0) return;

        const respondentIds = selectedRespondents.map(r => r.id);
        await addRespondentsToSurvey({
            surveyId,
            scheduleId,
            respondentIds
        });
    }, [selectedRespondents, surveyId, addRespondentsToSurvey, scheduleId]);

    const errorMessage = useMemo(() => {
        if (isLoadingSchedule) {
            return null; // Don't show error while loading
        }
        if (isErrorLoadingSchedule) {
            return "Failed to load schedule information.";
        }
        if (!companyId) {
            return "Schedule data is incomplete.";
        }
        if (selectedRespondents.length === 0) {
            return "Please select at least one respondent to add.";
        }
        return null;
    }, [companyId, selectedRespondents.length, isLoadingSchedule, isErrorLoadingSchedule]);

    const respondentFilter = useCallback((respondent: RespondentDto) => {
        if (existingRespondentIds.has(respondent.id)) {
            return false;
        }
        return !selectedRespondents.some(selected => selected.id === respondent.id);
    }, [selectedRespondents, existingRespondentIds]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <StandardDialogHeader
                icon={<PersonAdd fontSize='inherit' />}
                text="Add Respondents To Survey"
            />

            <DialogContent>
                <Box>
                    {isLoadingSchedule ? (
                        <Typography>Loading schedule information...</Typography>
                    ) : isErrorLoadingSchedule || !companyId ? (
                        <Typography color="error">Unable to load schedule information.</Typography>
                    ) : (
                        <Grid container direction="column" spacing={3}>
                            {existingRespondentIds.size > 0 && (
                                <Grid item container direction="column" spacing={2}>
                                    <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="h6">
                                            Current Respondents ({existingRespondentIds.size})
                                        </Typography>
                                        <Tooltip title="These are the respondents currently configured for this survey"><Info /></Tooltip>
                                    </Grid>
                                    <Grid item>
                                        <Alert severity="info" variant='outlined'>
                                            <Typography variant="body2">
                                                <strong>Important:</strong> When you add respondents to a survey instance,
                                                the respondent list will be updated for that instance and all future survey instances. However,
                                                if you view this list on an older survey instance, it may not reflect the
                                                respondents who actually received surveys for that specific instance.
                                            </Typography>
                                        </Alert>
                                    </Grid>
                                    <Grid item container spacing={1}>
                                        {existingRespondents.map((respondent) => (
                                            <Grid item xs="auto" key={respondent.id}>
                                                <Chip
                                                    label={`${respondent.firstName} ${respondent.lastName}`}
                                                    size="small"
                                                    variant="outlined"
                                                    style={{ backgroundColor: "#F0F0F0", borderColor: "#CCCCCC" }}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Grid>
                            )}

                            {companyId && (
                                <Grid item container direction="column" spacing={2}>
                                    <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="h6">
                                            Select Respondents To Add
                                        </Typography>
                                        <Tooltip title="These are the respondents you have selected to add to the survey. These respondents will be added to the selected survey instance and all future survey instances."><Info /></Tooltip>
                                    </Grid>
                                    <Grid item>
                                        <CompanyRespondentSelect
                                            key={selectedRespondents.length}
                                            companyId={companyId}
                                            selectedRespondentId={currentRespondentId}
                                            handleSelectedRespondentChange={handleRespondentSelected}
                                            itemFilter={respondentFilter}
                                        />
                                    </Grid>
                                    {selectedRespondents.length > 0 && <Grid item container spacing={1}>
                                        {selectedRespondents.map((respondent) => (
                                            <Grid item xs="auto" key={respondent.id}>
                                                <Chip
                                                    label={`${respondent.firstName} ${respondent.lastName}`}
                                                    size="small"
                                                    variant="outlined"
                                                    style={{ backgroundColor: "#E9E9E9", borderColor: "#707070" }}
                                                    onDelete={() => removeFromSelectedRespondents(respondent)}
                                                    deleteIcon={<CancelIcon style={{ color: "#333333" }} />}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>}
                                </Grid>
                            )}

                            {errorMessage && (
                                <Grid item>
                                    <Typography variant="body1" sx={{ color: '#DF623B' }}>
                                        {errorMessage}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={isAddingRespondents} variant="outlined">
                    Cancel
                </Button>
                <Button
                    onClick={handleAddRespondents}
                    variant="contained"
                    disabled={!companyId || selectedRespondents.length === 0 || isAddingRespondents}
                    sx={{ width: '196px !important' }}
                >
                    {isAddingRespondents ? 'Adding...' : `Add ${selectedRespondents.length} Respondent${selectedRespondents.length !== 1 ? 's' : ''}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
